<!--
/**
 * APCWO customisation for OrangeHRM — Personal Review module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <div class="orangehrm-background-container apcwo-pr-root">
    <div class="orangehrm-card-container apcwo-pr-form">
      <div class="apcwo-pr-form__head">
        <div>
          <oxd-text tag="h6" class="orangehrm-main-title">
            Personal Review &amp; Planning
          </oxd-text>
          <div v-if="review" class="apcwo-pr-form__sub">
            {{ review.cycle.name }}
          </div>
        </div>
        <div v-if="review" class="apcwo-pr-form__head-right">
          <status-badge :status="review.status" />
          <download-buttons
            v-if="review.permissions.downloadEmployee"
            :review-id="review.id"
            scope="employee"
          />
        </div>
      </div>
      <oxd-divider />

      <template v-if="review">
        <div
          v-if="review.unlockReason && review.status === 'draft'"
          class="apcwo-pr-banner apcwo-pr-banner--warn"
        >
          HR reopened this review for changes: {{ review.unlockReason }}
        </div>
        <div v-if="!editable" class="apcwo-pr-banner">
          <template v-if="review.cycle.status === 'closed'">
            This review cycle is closed.
          </template>
          <template v-else>
            Your review was submitted on {{ review.submittedAt }} and is locked.
            If you need to change something, ask HR to unlock it.
          </template>
        </div>

        <oxd-grid :cols="3" class="orangehrm-full-width-grid apcwo-pr-facts">
          <oxd-grid-item>
            <span class="apcwo-pr-facts__label">Employee</span>
            <span class="apcwo-pr-facts__value">{{
              review.employee.name
            }}</span>
          </oxd-grid-item>
          <oxd-grid-item>
            <span class="apcwo-pr-facts__label">Team</span>
            <span class="apcwo-pr-facts__value">{{ review.team || '—' }}</span>
          </oxd-grid-item>
          <oxd-grid-item>
            <span class="apcwo-pr-facts__label">Role</span>
            <span class="apcwo-pr-facts__value">{{
              review.roleTitle || '—'
            }}</span>
          </oxd-grid-item>
          <oxd-grid-item>
            <span class="apcwo-pr-facts__label">Manager</span>
            <span class="apcwo-pr-facts__value">{{
              review.managerName || '—'
            }}</span>
          </oxd-grid-item>
          <oxd-grid-item>
            <span class="apcwo-pr-facts__label">Review period</span>
            <span class="apcwo-pr-facts__value"
              >{{ formatDate(review.cycle.periodStart) }} –
              {{ formatDate(review.cycle.periodEnd) }}</span
            >
          </oxd-grid-item>
          <oxd-grid-item>
            <span class="apcwo-pr-facts__label">Due</span>
            <span class="apcwo-pr-facts__value">{{
              formatDate(review.cycle.dueDate) || '—'
            }}</span>
          </oxd-grid-item>
        </oxd-grid>

        <ol class="apcwo-pr-steps">
          <li v-for="(s, i) in steps" :key="s.key">
            <button
              type="button"
              :class="[
                'apcwo-pr-steps__item',
                {'is-active': i === step, 'is-done': stepDone(i)},
              ]"
              @click="goTo(i)"
            >
              <span class="apcwo-pr-steps__num">{{ i + 1 }}</span>
              {{ s.label }}
            </button>
          </li>
        </ol>

        <!-- Step 1 -->
        <section v-show="step === 0">
          <h3 class="apcwo-pr-section">{{ schema.lifeVision.title }}</h3>
          <p class="apcwo-pr-hint">{{ schema.lifeVision.instruction }}</p>
          <oxd-input-field
            v-model="form.lifeVision"
            type="textarea"
            label="MY PERSONAL LIFE VISION (3 SENTENCES OR LESS)"
            :disabled="!editable"
            class="apcwo-pr-field"
          />
          <oxd-input-field
            v-model="form.apcHelp"
            type="textarea"
            :label="schema.apcHelpLabel"
            :disabled="!editable"
            class="apcwo-pr-field"
          />
          <h3 class="apcwo-pr-section">{{ schema.spiritual.title }}</h3>
          <p class="apcwo-pr-hint">{{ schema.spiritual.instruction }}</p>
          <growth-rows
            v-model="form.spiritualGoals"
            head-objective="Spiritual Growth Objective"
            :disabled="!editable"
          />
          <h3 class="apcwo-pr-section">{{ schema.professional.title }}</h3>
          <p class="apcwo-pr-hint">{{ schema.professional.instruction }}</p>
          <growth-rows
            v-model="form.professionalGoals"
            head-objective="Professional Growth Objective"
            :disabled="!editable"
          />
        </section>

        <!-- Step 2 -->
        <section v-show="step === 1">
          <template v-for="key in step2Keys" :key="key">
            <h3 class="apcwo-pr-section">
              {{ schema.textSections[key].title }}
            </h3>
            <p class="apcwo-pr-hint">
              {{ schema.textSections[key].instruction }}
            </p>
            <oxd-input-field
              v-model="form[key]"
              type="textarea"
              :disabled="!editable"
              class="apcwo-pr-field"
            />
          </template>
        </section>

        <!-- Step 3 -->
        <section v-show="step === 2">
          <template v-for="key in step3Keys" :key="key">
            <h3 class="apcwo-pr-section">
              {{ schema.textSections[key].title }}
            </h3>
            <p class="apcwo-pr-hint">
              {{ schema.textSections[key].instruction }}
            </p>
            <oxd-input-field
              v-model="form[key]"
              type="textarea"
              :disabled="!editable"
              class="apcwo-pr-field"
            />
          </template>
        </section>

        <!-- Step 4 -->
        <section v-show="step === 3">
          <h3 class="apcwo-pr-section">Employee Evaluation</h3>
          <p class="apcwo-pr-hint">
            How would you rate the following on a scale of 1–5? (1 – lowest
            level, 5 – highest level)
          </p>
          <div
            v-for="r in schema.ratings"
            :key="r.key"
            class="apcwo-pr-rate-row"
          >
            <span>{{ r.label }}</span>
            <rating-input v-model="form.ratings[r.key]" :readonly="!editable" />
          </div>
          <template v-for="q in schema.evaluationText" :key="q.key">
            <h3 class="apcwo-pr-section apcwo-pr-section--sm">
              {{ q.label.toUpperCase() }}
            </h3>
            <oxd-input-field
              v-model="form[q.key]"
              type="textarea"
              :disabled="!editable"
              class="apcwo-pr-field"
            />
          </template>
        </section>

        <!-- Step 5 -->
        <section v-show="step === 4">
          <h3 class="apcwo-pr-section">REVIEW &amp; SUBMIT</h3>
          <div
            v-if="missing.length"
            class="apcwo-pr-banner apcwo-pr-banner--warn"
          >
            <strong>Still to complete before you can submit:</strong>
            <ul>
              <li v-for="m in missing" :key="m.text">
                <a href="#" @click.prevent="goTo(m.step)">{{ m.text }}</a>
              </li>
            </ul>
          </div>
          <div v-else-if="editable" class="apcwo-pr-banner apcwo-pr-banner--ok">
            Everything required is filled in. Once submitted, your review is
            locked and sent to HR.
          </div>
          <div v-else class="apcwo-pr-banner">
            This review has been submitted.
          </div>

          <div
            v-if="editable && confirming"
            class="apcwo-pr-banner apcwo-pr-banner--warn"
          >
            Submit your review to HR now? You will not be able to edit it unless
            HR unlocks it.
            <div class="apcwo-pr-confirm">
              <oxd-button
                label="Yes, submit"
                display-type="secondary"
                :disabled="isLoading"
                @click="submit"
              />
              <oxd-button
                label="Cancel"
                display-type="ghost"
                @click="confirming = false"
              />
            </div>
          </div>
        </section>

        <oxd-divider />
        <div class="apcwo-pr-actions">
          <oxd-button
            v-if="step > 0"
            label="Back"
            display-type="ghost"
            @click="goTo(step - 1)"
          />
          <span class="apcwo-pr-actions__spacer"></span>
          <oxd-button
            v-if="editable"
            label="Save draft"
            display-type="ghost"
            :disabled="isLoading"
            @click="saveDraft(true)"
          />
          <oxd-button
            v-if="step < steps.length - 1"
            label="Next"
            display-type="secondary"
            @click="goTo(step + 1)"
          />
          <oxd-button
            v-else-if="editable"
            label="SUBMIT"
            display-type="secondary"
            :disabled="isLoading || missing.length > 0 || confirming"
            @click="confirming = true"
          />
          <oxd-button label="Close" display-type="ghost" @click="close" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import StatusBadge from '../components/StatusBadge.vue';
import DownloadButtons from '../components/DownloadButtons.vue';
import RatingInput from '../components/RatingInput.vue';
import GrowthRows from '../components/GrowthRows.vue';
import {API_BASE, errorMessage, formatDate} from '../util/helpers';

const STEPS = [
  {key: 'vision', label: 'Vision & growth'},
  {key: 'review', label: 'Role & review'},
  {key: 'plans', label: 'Plans'},
  {key: 'evaluation', label: 'Self-evaluation'},
  {key: 'submit', label: 'Review & submit'},
];

export default {
  name: 'ReviewForm',
  components: {
    'status-badge': StatusBadge,
    'download-buttons': DownloadButtons,
    'rating-input': RatingInput,
    'growth-rows': GrowthRows,
  },
  props: {
    reviewId: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `${API_BASE}/reviews`,
    );
    return {http};
  },
  data() {
    return {
      review: null,
      schema: null,
      form: null,
      dirty: false,
      step: 0,
      steps: STEPS,
      isLoading: false,
      confirming: false,
      step2Keys: ['currentRoles', 'progress', 'improvements', 'courses'],
      step3Keys: ['nextYearGoals', 'detailedPlan', 'threeYearPlan'],
    };
  },
  computed: {
    editable() {
      return !!(this.review && this.review.permissions.editEmployee);
    },
    missing() {
      if (!this.form || !this.schema) return [];
      const out = [];
      const f = this.form;
      const blank = (v) => !v || String(v).trim() === '';
      if (blank(f.lifeVision)) {
        out.push({step: 0, text: 'Personal life vision'});
      }
      if (blank(f.apcHelp)) {
        out.push({
          step: 0,
          text: 'How can APC help me fulfil my personal life vision',
        });
      }
      const hasGoal = (rows) =>
        rows.some((r) => !blank(r.objective) && !blank(r.action));
      if (!hasGoal(f.spiritualGoals)) {
        out.push({
          step: 0,
          text: 'At least one spiritual growth objective with its action',
        });
      }
      if (!hasGoal(f.professionalGoals)) {
        out.push({
          step: 0,
          text: 'At least one professional growth objective with its action',
        });
      }
      this.step2Keys.forEach((k) => {
        if (blank(f[k])) {
          out.push({step: 1, text: this.schema.textSections[k].title});
        }
      });
      this.step3Keys.forEach((k) => {
        if (blank(f[k])) {
          out.push({step: 2, text: this.schema.textSections[k].title});
        }
      });
      this.schema.ratings.forEach((r) => {
        if (!f.ratings[r.key]) {
          out.push({step: 3, text: `Rating: ${r.label}`});
        }
      });
      return out;
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (this.loadedOnce) this.dirty = true;
      },
    },
  },
  created() {
    this.loadedOnce = false;
    this.isLoading = true;
    this.http
      .get(this.reviewId)
      .then((response) => this.apply(response.data.data))
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    formatDate,
    apply(review) {
      this.review = review;
      this.schema = review.schema;
      this.form = JSON.parse(JSON.stringify(review.employeeData));
      this.$nextTick(() => {
        this.dirty = false;
        this.loadedOnce = true;
      });
    },
    stepDone(i) {
      return !this.missing.some((m) => m.step === i) && i < 4;
    },
    async goTo(i) {
      if (this.editable && this.dirty) {
        const ok = await this.saveDraft(false);
        if (!ok) return;
      }
      this.confirming = false;
      this.step = i;
      window.scrollTo({top: 0, behavior: 'smooth'});
    },
    saveDraft(notify) {
      if (!this.editable) return Promise.resolve(true);
      this.isLoading = true;
      return this.http
        .update(this.reviewId, {section: 'employee', data: this.form})
        .then((response) => {
          this.apply(response.data.data);
          if (notify) this.$toast.saveSuccess();
          return true;
        })
        .catch(() => false)
        .finally(() => {
          this.isLoading = false;
        });
    },
    submit() {
      this.isLoading = true;
      const actions = new APIService(
        window.appGlobal.baseUrl,
        `${API_BASE}/reviews/${this.reviewId}/actions`,
      );
      actions
        .create({action: 'submit', data: this.form})
        .then((response) => {
          this.apply(response.data.data);
          this.confirming = false;
          this.$toast.success({
            title: 'Submitted',
            message: 'Your review has been submitted to HR.',
          });
        })
        .catch((e) => {
          this.$toast.error({title: 'Error', message: errorMessage(e)});
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    close() {
      navigate('/personalReview/viewMyPersonalReview');
    },
  },
};
</script>

<style>
.apcwo-pr-form__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding-bottom: 4px;
}
.apcwo-pr-form__head-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.apcwo-pr-form__sub {
  margin-top: 4px;
  font-size: 13px;
  color: #6b6b7a;
}
.apcwo-pr-banner {
  margin: 12px 0 16px 0;
  padding: 12px 16px;
  border-radius: 8px;
  background: #eef0f6;
  line-height: 1.5;
  font-size: 14px;
}
.apcwo-pr-banner--warn {
  background: #fff3d6;
  color: #6f4a00;
}
.apcwo-pr-banner--ok {
  background: #d9f2e0;
  color: #1b6b35;
}
.apcwo-pr-banner ul {
  margin: 6px 0 0 18px;
  padding: 0;
}
.apcwo-pr-confirm {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
.apcwo-pr-facts {
  margin: 4px 0 20px 0;
  padding: 16px 20px;
  border: 1px solid #ebebf0;
  border-radius: 8px;
  background: #fafafc;
  font-size: 14px;
  row-gap: 18px;
}
.apcwo-pr-facts__label {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8a8a99;
}
.apcwo-pr-facts__value {
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: #2b2b36;
}
.apcwo-pr-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 24px 0;
  padding: 0;
  list-style: none;
}
.apcwo-pr-steps__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 8px;
  border: 1px solid #d5d5de;
  border-radius: 20px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #4b4b5a;
  cursor: pointer;
}
.apcwo-pr-steps__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e8e8ee;
  font-size: 12px;
}
.apcwo-pr-steps__item.is-done .apcwo-pr-steps__num {
  background: #d9f2e0;
  color: #1b6b35;
}
.apcwo-pr-steps__item.is-active {
  border-color: #741e1e;
  background: #741e1e;
  color: #fff;
}
.apcwo-pr-steps__item.is-active .apcwo-pr-steps__num {
  background: #fff;
  color: #741e1e;
}
.apcwo-pr-section {
  margin: 22px 0 4px 0;
  font-size: 16px;
  font-weight: 700;
  color: #741e1e;
}
.apcwo-pr-section:not(.apcwo-pr-section--sm) {
  text-transform: uppercase;
}
.apcwo-pr-section--sm {
  font-size: 14px;
  color: #2b2b36;
}
.apcwo-pr-hint {
  margin: 0 0 10px 0;
  font-size: 13px;
  font-style: italic;
  color: #7a7a89;
}
.apcwo-pr-field {
  margin-bottom: 14px;
}
.apcwo-pr-field textarea {
  min-height: 110px;
}
.apcwo-pr-rate-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}
.apcwo-pr-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.apcwo-pr-actions__spacer {
  flex: 1;
}
.apcwo-pr-root {
  font-family: 'Nunito Sans', sans-serif;
}
.apcwo-pr-root button,
.apcwo-pr-root input,
.apcwo-pr-root textarea,
.apcwo-pr-root select {
  font-family: inherit;
}
</style>
